import React from 'react';
import {
  Modal,
  Descriptions,
  Tag,
  Card,
  Button,
  Alert,
  Row,
  Col,
  Statistic,
  Typography,
  Space,
  message
} from 'antd';
import {
  GoogleOutlined,
  FacebookOutlined,
  BarChartOutlined,
  DatabaseOutlined,
  CopyOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { Text } = Typography;

const AuditModal = ({ visible, audit, onClose, getStatusColor, getStatusText }) => {
  if (!audit) return null;

  // Gelişmiş markdown renderer
  const renderMarkdown = (text) => {
    if (!text) return '';
    
    let html = text;
    
    // Tables (| Header | Header |)
    html = html.replace(
      /(\|[^\n]+\|\n)(\|[-:\s|]+\|\n)((\|[^\n]+\|\n?)+)/gm,
      (match, header, separator, rows) => {
        const headerCells = header.split('|').slice(1, -1).map(cell => 
          `<th style="padding: 8px 12px; border: 1px solid #d9d9d9; background: #fafafa; font-weight: bold;">${cell.trim()}</th>`
        ).join('');
        
        const bodyRows = rows.trim().split('\n').map(row => {
          const cells = row.split('|').slice(1, -1).map(cell => 
            `<td style="padding: 8px 12px; border: 1px solid #d9d9d9;">${cell.trim()}</td>`
          ).join('');
          return `<tr>${cells}</tr>`;
        }).join('');
        
        return `<table style="border-collapse: collapse; width: 100%; margin: 16px 0; font-size: 14px;">
          <thead><tr>${headerCells}</tr></thead>
          <tbody>${bodyRows}</tbody>
        </table>`;
      }
    );
    
    // Code blocks (```code```)
    html = html.replace(
      /```([\s\S]*?)```/g, 
      '<pre style="background: #f6f8fa; padding: 16px; border-radius: 6px; border: 1px solid #e1e4e8; margin: 16px 0; overflow-x: auto;"><code style="font-family: SFMono-Regular, Consolas, Monaco, monospace; font-size: 13px; line-height: 1.45;">$1</code></pre>'
    );
    
    // Inline code (`code`)
    html = html.replace(
      /`([^`]+)`/g, 
      '<code style="background: #f6f8fa; padding: 2px 4px; border-radius: 3px; font-family: SFMono-Regular, Consolas, Monaco, monospace; font-size: 85%; border: 1px solid #e1e4e8;">$1</code>'
    );
    
    // Bold text (**text**)
    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Italic text (*text*)
    html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');
    
    // Headers
    html = html.replace(/^#### (.*$)/gm, '<h5 style="margin: 12px 0 6px 0; font-size: 13px; font-weight: bold; color: #1890ff;">$1</h5>');
    html = html.replace(/^### (.*$)/gm, '<h4 style="margin: 12px 0 6px 0; font-size: 14px; font-weight: bold; color: #1890ff;">$1</h4>');
    html = html.replace(/^## (.*$)/gm, '<h3 style="margin: 16px 0 8px 0; font-size: 16px; font-weight: bold; color: #1890ff;">$1</h3>');
    html = html.replace(/^# (.*$)/gm, '<h2 style="margin: 20px 0 10px 0; font-size: 18px; font-weight: bold; color: #1890ff;">$1</h2>');
    
    // Links [text](url)
    html = html.replace(
      /\[([^\]]+)\]\(([^\)]+)\)/g, 
      '<a href="$2" style="color: #1890ff; text-decoration: none;" target="_blank" rel="noopener noreferrer">$1</a>'
    );
    
    // Blockquotes (> text)
    html = html.replace(
      /^> (.*$)/gm, 
      '<blockquote style="border-left: 4px solid #1890ff; margin: 16px 0; padding: 8px 16px; background: #f6f8fa; font-style: italic;">$1</blockquote>'
    );
    
    // Horizontal rules (---)
    html = html.replace(/^---$/gm, '<hr style="border: none; border-top: 1px solid #e1e4e8; margin: 24px 0;" />');
    
    // Unordered lists (- text or * text)
    html = html.replace(/^[\-\*] (.*$)/gm, '<li style="margin: 4px 0; list-style-type: disc; margin-left: 20px;">$1</li>');
    
    // Ordered lists (1. text)
    html = html.replace(/^(\d+)\. (.*$)/gm, '<li style="margin: 4px 0; list-style-type: decimal; margin-left: 20px;">$2</li>');
    
    // Wrap consecutive list items in ul/ol tags
    html = html.replace(/((<li[^>]*>.*<\/li>\s*){2,})/g, '<ul style="margin: 8px 0; padding-left: 0;">$1</ul>');
    
    // Line breaks
    html = html.replace(/\n/g, '<br/>');
    
    return html;
  };

  const handleCopyAnalysis = () => {
    if (audit.analysis) {
      navigator.clipboard.writeText(audit.analysis);
      message.success('Analiz metni panoya kopyalandı');
    }
  };

  const handleCopyPrompt = () => {
    if (audit.prompt) {
      navigator.clipboard.writeText(audit.prompt);
      message.success('Prompt metni panoya kopyalandı');
    }
  };

  const getAuditIcon = () => {
    if (audit.audit_type === 'google_ads') {
      return <GoogleOutlined style={{ color: '#4285F4' }} />;
    }
    if (audit.audit_type === 'facebook') {
      return <FacebookOutlined style={{ color: '#1877F2' }} />;
    }
    if (audit.audit_type === 'google_analytics') {
      return <BarChartOutlined style={{ color: '#EA4335' }} />;
    }
    if (audit.audit_type === 'custom_data') {
      return <DatabaseOutlined style={{ color: '#722ED1' }} />;
    }
    return <FacebookOutlined style={{ color: '#1877F2' }} />;
  };

  const getAuditTypeName = () => {
    if (audit.audit_type === 'google_ads') return 'Google Ads';
    if (audit.audit_type === 'facebook') return 'Facebook Ads';
    if (audit.audit_type === 'google_analytics') return 'Google Analytics';
    if (audit.audit_type === 'custom_data') return 'ReportsUp';
    return 'Bilinmiyor';
  };

  const formatModelName = (modelName) => {
    if (!modelName) return 'Bilinmiyor';
    
    // Extract readable name from model path
    const parts = modelName.split('/');
    const lastPart = parts[parts.length - 1];
    
    // Format common model names
    const modelMappings = {
      'gemini-2.0-flash-exp': 'Gemini 2.0 Flash (Experimental)',
      'gemini-pro': 'Gemini Pro',
      'gpt-4o': 'GPT-4 Omni',
      'gpt-4o-mini': 'GPT-4 Omni Mini',
      'claude-3-5-sonnet': 'Claude 3.5 Sonnet'
    };
    
    return modelMappings[lastPart] || lastPart;
  };

  return (
    <Modal
      title={
        <Space>
          {getAuditIcon()}
          <span>{audit.Brand?.name || 'Bilinmiyor'} - {getAuditTypeName()} Audit</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={1000}
      footer={[
        <Button key="close" onClick={onClose}>
          Kapat
        </Button>,
        ...(audit.prompt ? [
          <Button 
            key="copy-prompt" 
            icon={<CopyOutlined />} 
            onClick={handleCopyPrompt}
          >
            Prompt'u Kopyala
          </Button>
        ] : []),
        ...(audit.analysis ? [
          <Button 
            key="copy-analysis" 
            icon={<CopyOutlined />} 
            onClick={handleCopyAnalysis}
            type="primary"
          >
            Analizi Kopyala
          </Button>
        ] : [])
      ]}
    >
      {/* Basic Information */}
      <Descriptions bordered column={2} style={{ marginBottom: 16 }}>
        <Descriptions.Item label="Hesap ID">{audit.account_id}</Descriptions.Item>
        <Descriptions.Item label="Durum">
          <Tag 
            color={getStatusColor(audit.status)}
            style={{ borderRadius: '12px', fontWeight: 'bold' }}
          >
            {getStatusText(audit.status)}
          </Tag>
        </Descriptions.Item>
        
        <Descriptions.Item label="Model">
          {formatModelName(audit.model)}
        </Descriptions.Item>
        <Descriptions.Item label="Yanıt Süresi">
          {audit.response_time || 'Bilinmiyor'}
        </Descriptions.Item>
        
        <Descriptions.Item label="Tarih Aralığı">
          {audit.date_range || 'Varsayılan'}
        </Descriptions.Item>
        <Descriptions.Item label="Oluşturulma Tarihi">
          {dayjs(audit.created_at).format('DD.MM.YYYY HH:mm:ss')}
        </Descriptions.Item>
      </Descriptions>

      {/* Prompt */}
      <Card 
        title="Kullanılan Prompt" 
        size="small" 
        style={{ marginBottom: 16 }}
        extra={
          <Button 
            size="small" 
            icon={<CopyOutlined />} 
            onClick={handleCopyPrompt}
          >
            Kopyala
          </Button>
        }
      >
        <div style={{
          background: '#f5f5f5',
          padding: '12px',
          borderRadius: '6px',
          whiteSpace: 'pre-wrap',
          fontSize: '13px',
          lineHeight: '1.5',
          maxHeight: '200px',
          overflowY: 'auto'
        }}>
          {audit.prompt}
        </div>
      </Card>

      {/* Success Result */}
      {audit.status === 'success' && audit.analysis && (
        <Card 
          title="Analiz Sonucu" 
          size="small"
          extra={
            <Button 
              size="small" 
              icon={<CopyOutlined />} 
              onClick={handleCopyAnalysis}
            >
              Kopyala
            </Button>
          }
        >
          <div style={{
            background: '#fafafa',
            padding: '16px',
            borderRadius: '6px',
            fontSize: '14px',
            lineHeight: '1.6',
            maxHeight: '400px',
            overflowY: 'auto'
          }}>
            <div 
              dangerouslySetInnerHTML={{ 
                __html: renderMarkdown(audit.analysis) 
              }}
            />
          </div>
        </Card>
      )}

      {/* Error Details */}
      {audit.status === 'error' && audit.error_message && (
        <Card title="Hata Detayı" size="small">
          <Alert 
            type="error" 
            message={audit.error_message}
            showIcon
            style={{ fontSize: '13px' }}
          />
        </Card>
      )}

      {/* Pending Status */}
      {audit.status === 'pending' && (
        <Card title="Durum" size="small">
          <Alert 
            type="info" 
            message="Bu audit henüz tamamlanmadı. Lütfen daha sonra tekrar kontrol edin."
            showIcon
          />
        </Card>
      )}

      {/* Token Usage Statistics */}
      {audit.usage_stats && (
        <Card title="Token Kullanımı" size="small" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={8}>
              <Statistic 
                title="Prompt Tokens" 
                value={audit.usage_stats.prompt_tokens || 0}
                valueStyle={{ fontSize: '16px' }}
              />
            </Col>
            <Col span={8}>
              <Statistic 
                title="Completion Tokens" 
                value={audit.usage_stats.completion_tokens || 0}
                valueStyle={{ fontSize: '16px' }}
              />
            </Col>
            <Col span={8}>
              <Statistic 
                title="Toplam Tokens" 
                value={audit.usage_stats.total_tokens || 0}
                valueStyle={{ fontSize: '16px', color: '#1890ff' }}
              />
            </Col>
          </Row>
        </Card>
      )}
    </Modal>
  );
};

export default AuditModal;